
import { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowUp, ArrowDown, History } from 'lucide-react';
import { getInventoryLogs, InventoryLog } from '@/lib/supabase/inventory';
import { useToast } from '@/components/ui/use-toast';

interface InventoryHistoryProps {
  productId: string;
}

const InventoryHistory = ({ productId }: InventoryHistoryProps) => {
  const [logs, setLogs] = useState<InventoryLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchLogs = async () => {
      if (!productId) return;

      setIsLoading(true);
      try {
        const data = await getInventoryLogs(productId);
        setLogs(data);
      } catch (error) {
        console.error('Failed to fetch inventory logs:', error);
        toast({
          title: 'Error',
          description: 'Failed to load inventory history',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
  }, [productId, toast]);

  const getChangeColor = (change: number) => {
    return change > 0 
      ? 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400' 
      : change < 0 
        ? 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400' 
        : 'text-gray-500 bg-gray-50 dark:bg-gray-800/50 dark:text-gray-400';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-md flex items-center gap-2">
          <History className="h-4 w-4" />
          Inventory History
        </CardTitle>
        <CardDescription>
          Track inventory changes over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500 dark:border-gray-400"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            No inventory changes recorded yet
          </div>
        ) : (
          <div className="reference-table-container max-h-[400px]">
            <Table>
              <TableHeader className="reference-table-header">
                <TableRow>
                  <TableHead className="reference-table-cell-header">Date</TableHead>
                  <TableHead className="reference-table-cell-header">Change</TableHead>
                  <TableHead className="reference-table-cell-header">Stock After</TableHead>
                  <TableHead className="reference-table-cell-header">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="reference-table-row">
                    <TableCell className="whitespace-nowrap reference-table-cell">
                      {formatDate(log.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`flex items-center gap-1 ${getChangeColor(log.quantity_change)}`}
                      >
                        {log.quantity_change > 0 ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )}
                        {log.quantity_change > 0 ? '+' : ''}{log.quantity_change}
                      </Badge>
                    </TableCell>
                    <TableCell className="reference-table-cell">
                      {log.new_quantity}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate reference-table-cell">
                      {log.reason || 'Manual adjustment'}
                      {log.notes && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                          {log.notes}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InventoryHistory;
